// Row 4.7 — in-process sub-app crash isolation (G33 inline-lane half). Wraps the sub-app
// mount lanes in a real React error boundary (@perlab/ui ChromeErrorBoundary /
// react-error-boundary) so a crashing sub-app renders a contained error surface instead of
// white-screening the shell. keyed by app id: switching apps discards a crashed boundary.
//
// HONEST SCOPE (disclosed, CFR-tracked): React error boundaries catch RENDER-PHASE /
// lifecycle errors only — event-handler, setTimeout and rejected-promise crashes bypass any
// boundary (CFR: window.onerror/unhandledrejection strategy). Iframe-lane crashes can't cross
// the document boundary at all (CFR: handshake/timeout detection); the iframe lane is a
// flag-gated placeholder today. Retry on a deterministically-crashing app re-crashes to the
// same fallback — contained, not resolved.
import { type ReactNode } from "react";
import { ChromeErrorBoundary } from "@perlab/ui";

interface SubAppBoundaryProps {
  app: string;
  children: ReactNode;
  fillAvailableHeight?: boolean;
}

export function SubAppBoundary({
  app,
  children,
  fillAvailableHeight = false,
}: SubAppBoundaryProps): React.ReactElement {
  return (
    <div
      data-app="subapp-boundary"
      data-subapp={app}
      className={fillAvailableHeight ? "h-full min-h-0 overflow-hidden" : undefined}
    >
      <ChromeErrorBoundary key={app}>{children}</ChromeErrorBoundary>
    </div>
  );
}
